import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock, Download, RefreshCw, LogOut, Search, ArrowLeft,
  Users, TrendingUp, Mail, Loader2, BarChart3, Table as TableIcon, CheckCircle, Inbox, Rocket,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import AnalyticsTab, { FilterBar } from "@/components/AnalyticsTab";
import MessagesTab from "@/components/MessagesTab";
import PilotTab from "@/components/PilotTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "gf_admin_token";

const TokenGate = ({ onUnlock }) => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      await axios.get(`${API}/waitlist/list?limit=1`, {
        headers: { "X-Admin-Token": token.trim() },
      });
      localStorage.setItem(STORAGE_KEY, token.trim());
      toast.success("Welcome back, captain 🫡");
      onUnlock(token.trim());
    } catch (err) {
      toast.error("Invalid token", { description: "Double-check ADMIN_TOKEN in backend/.env" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-spotlight grid place-items-center px-6">
      <form
        onSubmit={submit}
        className="glass rounded-3xl p-8 w-full max-w-md edge-glow-soft"
        data-testid="admin-gate-form"
      >
        <div className="w-14 h-14 rounded-2xl bg-[#00FF88]/10 border border-[#00FF88]/20 grid place-items-center mb-5">
          <Lock className="w-6 h-6 text-[#00FF88]" />
        </div>
        <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-2">
          // Admin · Waitlist console
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Enter admin token</h1>
        <p className="text-white/55 text-sm mt-2 mb-6">
          The token lives in <span className="font-mono-c text-[#00FF88]">backend/.env</span> as
          <span className="font-mono-c text-[#00FF88]"> ADMIN_TOKEN</span>.
        </p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="gf_..."
          className="w-full h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-full text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 font-mono-c text-sm"
          data-testid="admin-token-input"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full h-12 bg-[#00FF88] text-black font-bold rounded-full btn-glow disabled:opacity-60 inline-flex items-center justify-center gap-2"
          data-testid="admin-unlock-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock console →"}
        </button>
        <Link
          to="/"
          className="block mt-5 text-center text-xs text-white/40 hover:text-white font-mono-c"
        >
          ← back to landing
        </Link>
      </form>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="glass rounded-2xl p-5 flex items-center gap-4" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
    <div className="w-12 h-12 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20 grid place-items-center">
      <Icon className="w-5 h-5 text-[#00FF88]" />
    </div>
    <div>
      <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-display font-extrabold text-2xl">{value}</div>
    </div>
  </div>
);

const Console = ({ token, onLogout }) => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [count, setCount] = useState({ count: 0, completed: 0, displayCount: 0 });
  const [tab, setTab] = useState("signups");
  const [filters, setFilters] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        axios.get(`${API}/waitlist/list?limit=2000`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/waitlist/count`),
      ]);
      setData(list.data);
      setCount(c.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Token expired / invalid");
        onLogout();
      } else {
        toast.error("Failed to load waitlist");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/waitlist/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "gigafloat-waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded 📥");
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadXlsx = async () => {
    try {
      const res = await axios.get(`${API}/waitlist/export.xlsx`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "gigafloat-waitlist.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded 📊");
    } catch {
      toast.error("Excel export failed");
    }
  };

  const filtered = data.items.filter((i) => {
    // Text search
    if (q) {
      const s = q.toLowerCase();
      const hit =
        String(i.email || "").toLowerCase().includes(s) ||
        String(i.full_name || "").toLowerCase().includes(s) ||
        String(i.platform || "").toLowerCase().includes(s) ||
        String(i.source || "").toLowerCase().includes(s) ||
        String(i.persona || "").toLowerCase().includes(s);
      if (!hit) return false;
    }
    // Field filters
    for (const [k, v] of Object.entries(filters)) {
      if (!v) continue;
      if (k === "high_intent") {
        if (!i.high_intent) return false;
        continue;
      }
      if (i[k] !== v) return false;
    }
    return true;
  });

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-console">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-home-link">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <Logo />
            <span className="ml-3 text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88] border border-[#00FF88]/30 rounded-full px-2 py-0.5">admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 border border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/10 font-bold text-sm px-5 py-2 rounded-full transition-colors"
              data-testid="admin-export-csv-button"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={downloadXlsx}
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full btn-glow"
              data-testid="admin-export-xlsx-button"
            >
              <Download className="w-4 h-4" />
              Export XLSX
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white border border-white/10 rounded-full px-4 py-2"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">
          // Waitlist console
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-6">
          {data.total.toLocaleString()} <span className="text-white/40">on the list.</span>
        </h1>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-white/5">
          {[
            { id: "signups",   label: "Signups",     icon: TableIcon },
            { id: "analytics", label: "Analytics",   icon: BarChart3 },
            { id: "pilot",     label: "Float Pilot", icon: Rocket },
            { id: "messages",  label: "Messages",    icon: Inbox },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 transition-colors text-sm font-semibold ${
                  active ? "border-[#00FF88] text-white" : "border-transparent text-white/40 hover:text-white/70"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "analytics" && <AnalyticsTab token={token} />}
        {tab === "pilot" && <PilotTab token={token} />}
        {tab === "messages" && <MessagesTab token={token} />}
        {tab === "signups" && (
        <>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Stat icon={Users}      label="Total signups"   value={data.total.toLocaleString()} />
          <Stat icon={CheckCircle} label="Completed wizard" value={count.completed.toLocaleString()} />
          <Stat icon={Mail}       label="Unique emails"   value={new Set(data.items.map(i => i.email)).size.toLocaleString()} />
        </div>

        <FilterBar data={data.items} filters={filters} setFilters={setFilters} />

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, name, platform, source..."
              className="w-full h-11 pl-11 pr-4 bg-[#0A0A0A] border border-white/10 rounded-full text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/40"
              data-testid="admin-search-input"
            />
          </div>
          <div className="text-xs font-mono-c text-white/40">
            Showing {filtered.length} of {data.total}
          </div>
        </div>

        <div className="glass rounded-2xl overflow-hidden" data-testid="admin-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/5">
                <tr className="text-left font-mono-c text-[10px] uppercase tracking-widest text-white/40">
                  <th className="px-5 py-3 w-14">#</th>
                  <th className="px-5 py-3">Email / Name</th>
                  <th className="px-5 py-3 w-28">Platform</th>
                  <th className="px-5 py-3 w-32">Earnings/wk</th>
                  <th className="px-5 py-3 w-24">Advance</th>
                  <th className="px-5 py-3 w-32">Urgency</th>
                  <th className="px-5 py-3 w-24">Source</th>
                  <th className="px-5 py-3 w-44">Joined</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-white/40">
                    <Loader2 className="w-5 h-5 animate-spin inline" />
                  </td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-white/40">
                    No signups yet — share the link!
                  </td></tr>
                )}
                {!loading && filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`admin-row-${r.id}`}
                  >
                    <td className="px-5 py-3 font-mono-c text-[#00FF88]">#{r.position}</td>
                    <td className="px-5 py-3">
                      <div className="font-medium flex items-center gap-2">
                        {r.email}
                        {r.high_intent && (
                          <span className="text-[9px] font-mono-c uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30">
                            ★ HIGH INTENT
                          </span>
                        )}
                      </div>
                      {r.full_name && <div className="text-[11px] text-white/45 font-mono-c mt-0.5">{r.full_name}{r.phone ? ` · ${r.phone}` : ""}</div>}
                    </td>
                    <td className="px-5 py-3">
                      {Array.isArray(r.platforms) && r.platforms.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {r.platforms.slice(0, 3).map((p) => (
                            <span key={p} className="text-[9px] font-mono-c uppercase px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{p}</span>
                          ))}
                          {r.platforms.length > 3 && <span className="text-[9px] text-white/40">+{r.platforms.length - 3}</span>}
                        </div>
                      ) : r.platform ? (
                        <span className="text-[10px] font-mono-c uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70">
                          {r.platform}
                        </span>
                      ) : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-5 py-3 text-white/70 text-xs font-mono-c">{r.earnings_range || "—"}</td>
                    <td className="px-5 py-3 text-white/70 text-xs font-mono-c">{r.ideal_advance ? `$${r.ideal_advance}` : "—"}</td>
                    <td className="px-5 py-3 text-white/70 text-xs">{r.urgency || "—"}</td>
                    <td className="px-5 py-3 text-white/70 text-xs">{r.source || "—"}</td>
                    <td className="px-5 py-3 text-white/55 font-mono-c text-xs">{fmt(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (!token) return <TokenGate onUnlock={setToken} />;
  return <Console token={token} onLogout={onLogout} />;
};

export default Admin;
